import { Divider } from 'antd';
import { DiagramEngine } from '@projectstorm/react-diagrams';
import { useEffect, useState, useCallback, useRef } from 'react';
import { observer } from 'mobx-react';

import ResetZoomIcon from '@/comp/common/icons/ResetZoomIcon';
import { EPButton } from '@/components';
import MinusIcon from '@/comp/common/icons/MinusIcon';
import PlusIcon from '@/comp/common/icons/PlusIcon';
import { useStores } from '@/hooks/use-stores';

import { ZoomButtonsContainer } from './ZoomButtons.styles';

const ZOOM_LEVEL_MULTIPLIER = 1.38;
const ZOOM_LEVEL_INCREMENT = 20;
const ZOOM_LEVEL_RESET = 45;
const ZOOM_UPDATE_THROTTLE = 16; // ~60fps

const ZoomButtons = observer(({ engine, resetZoom }: { engine: DiagramEngine; resetZoom: () => void }) => {
  const { pidEditStore } = useStores();
  const [zoomLevel, setZoomLevel] = useState(() => Math.floor(engine.getModel().getZoomLevel()));
  const updateTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Efficiently update zoom level display - only when zoom actually changes
  useEffect(() => {
    const model = engine.getModel();

    const updateZoomLevel = () => {
      const currentZoom = Math.floor(model.getZoomLevel());
      setZoomLevel(prev => (prev !== currentZoom ? currentZoom : prev));
    };

    // Use a throttled update mechanism
    const scheduleUpdate = () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      updateTimeoutRef.current = setTimeout(updateZoomLevel, ZOOM_UPDATE_THROTTLE);
    };

    // Listen to model changes
    const listener = {
      eventDidFire: scheduleUpdate,
    };

    const listenerHandle = model.registerListener(listener);

    // Also check periodically in case events don't fire
    const intervalId = setInterval(updateZoomLevel, 100);

    return () => {
      if (updateTimeoutRef.current) {
        clearTimeout(updateTimeoutRef.current);
      }
      clearInterval(intervalId);
      listenerHandle?.deregister();
    };
  }, [engine]);

  // fix for resetting zoom level when coming back to browser tab
  useEffect(() => {
    if (zoomLevel === ZOOM_LEVEL_RESET) resetZoom();
  }, [zoomLevel, resetZoom]);

  const handleZoomIn = useCallback(() => {
    const model = engine.getModel();
    const currentZoom = model.getZoomLevel();
    model.setZoomLevel(currentZoom + ZOOM_LEVEL_INCREMENT);
    if (pidEditStore.isActiveMenuEnabled) pidEditStore.hideMenu();
  }, [engine]);

  const handleZoomOut = useCallback(() => {
    const model = engine.getModel();
    const currentZoom = model.getZoomLevel();
    const newZoom = currentZoom - ZOOM_LEVEL_INCREMENT;
    if (newZoom >= 0) {
      model.setZoomLevel(newZoom);
    }
    if (pidEditStore.isActiveMenuEnabled) pidEditStore.hideMenu();
  }, [engine]);

  const handleResetZoom = useCallback(() => {
    resetZoom();
    if (pidEditStore.isActiveMenuEnabled) pidEditStore.hideMenu();
  }, [resetZoom]);

  return (
    <ZoomButtonsContainer>
      <div className="zoom-buttons-container">
        <EPButton
          icon={<PlusIcon width={24} height={24} />}
          size="medium"
          type="text"
          shape="circle"
          onClick={handleZoomIn}
        />
        <span className="zoom-level">{Math.ceil(zoomLevel * ZOOM_LEVEL_MULTIPLIER)}</span>
        <EPButton icon={<MinusIcon />} size="medium" type="text" shape="circle" onClick={handleZoomOut} />
      </div>
      <Divider className="divider" />
      <div className="reset-zoom-icon-container">
        <EPButton icon={<ResetZoomIcon />} size="medium" type="text" onClick={handleResetZoom} />
      </div>
    </ZoomButtonsContainer>
  );
});

export default ZoomButtons;
